import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, FormControl, InputLabel, Select, MenuItem, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, CircularProgress,
  Alert, Stack, Tooltip, Paper,
} from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { api, apiRequest } from '../../utils/api';

interface ContentReport {
  report_id: string;
  content_type: string;
  content_id: string;
  reason_code: string;
  reason_detail: string | null;
  status: string;
  priority: string;
  created_at: string;
  reviewed_at: string | null;
  resolution_note: string | null;
  reporter_name: string | null;
  reporter_email: string | null;
  reviewed_by_name: string | null;
}

const REASON_LABELS: Record<string, string> = {
  csam: 'Child sexual abuse', illegal: 'Illegal content', harassment: 'Harassment',
  hate: 'Hate speech', violence: 'Violence', sexual: 'Sexual content', spam: 'Spam', other: 'Other',
};
const STATUS_OPTIONS = ['open', 'reviewing', 'actioned', 'dismissed'];
const PRIORITY_OPTIONS = ['normal', 'high'];

const cap = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);

const statusColor = (s: string): 'default' | 'info' | 'success' | 'warning' => {
  switch (s) {
    case 'open': return 'warning';
    case 'reviewing': return 'info';
    case 'actioned': return 'success';
    default: return 'default';
  }
};

const ContentModerationTab: React.FC = () => {
  const [reports, setReports] = useState<ContentReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('open');
  const [priorityFilter, setPriorityFilter] = useState('');

  const [selected, setSelected] = useState<ContentReport | null>(null);
  const [editStatus, setEditStatus] = useState('');
  const [editPriority, setEditPriority] = useState('');
  const [editNote, setEditNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (priorityFilter) params.set('priority', priorityFilter);
      const qs = params.toString();
      const res = await api.get(`/admin/content-reports${qs ? `?${qs}` : ''}`);
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load reports');
      setReports(data.reports || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter]);

  useEffect(() => { load(); }, [load]);

  const openAction = (r: ContentReport) => {
    setSelected(r);
    setEditStatus(r.status);
    setEditPriority(r.priority);
    setEditNote(r.resolution_note || '');
  };
  const closeAction = () => { if (!saving) setSelected(null); };

  const saveAction = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await apiRequest(`/admin/content-reports/${selected.report_id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: editStatus, priority: editPriority, resolution_note: editNote || null }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Update failed');
      setSelected(null);
      await load();
    } catch (e: any) {
      setError(e.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const highOpenCount = reports.filter((r) => r.priority === 'high' && r.status === 'open').length;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <ShieldIcon sx={{ color: '#2D6E6A' }} />
        <Typography variant="h6" fontWeight={600}>Content Moderation</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        User-reported content (AUP &sect;5.5). Child-safety and illegal reports are flagged high-priority and sorted to the top.
      </Typography>

      {highOpenCount > 0 && (
        <Alert severity="error" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
          {highOpenCount} high-priority report{highOpenCount > 1 ? 's' : ''} awaiting review.
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{cap(s)}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Priority</InputLabel>
          <Select label="Priority" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {PRIORITY_OPTIONS.map((p) => <MenuItem key={p} value={p}>{cap(p)}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={load} sx={{ color: '#2D6E6A', borderColor: '#2D6E6A' }}>Refresh</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : reports.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          No reports match this filter.
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Priority</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Detail</TableCell>
                <TableCell>Reporter</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Reported</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((r) => {
                const isHighOpen = r.priority === 'high' && r.status === 'open';
                return (
                  <TableRow key={r.report_id} sx={isHighOpen ? { bgcolor: 'rgba(211,47,47,0.06)' } : undefined}>
                    <TableCell>
                      <Chip
                        size="small"
                        label={r.priority === 'high' ? 'HIGH' : 'Normal'}
                        color={r.priority === 'high' ? 'error' : 'default'}
                        variant={r.priority === 'high' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>{r.content_type}</TableCell>
                    <TableCell>{REASON_LABELS[r.reason_code] || r.reason_code}</TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      <Tooltip title={r.reason_detail || ''}>
                        <Typography variant="body2" noWrap>{r.reason_detail || '\u2014'}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={r.reporter_email || ''}>
                        <span>{r.reporter_name || 'Unknown'}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell><Chip size="small" label={r.status} color={statusColor(r.status)} /></TableCell>
                    <TableCell>{new Date(r.created_at).toLocaleDateString()}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => openAction(r)}
                        sx={{ bgcolor: '#2D6E6A', '&:hover': { bgcolor: '#1F4E4A' } }}
                      >
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={!!selected} onClose={closeAction} maxWidth="sm" fullWidth>
        <DialogTitle>Review report</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Content</Typography>
                <Typography variant="body2">{selected.content_type} &middot; {selected.content_id}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Reason</Typography>
                <Typography variant="body2">{REASON_LABELS[selected.reason_code] || selected.reason_code}</Typography>
                {selected.reason_detail && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>{selected.reason_detail}</Typography>
                )}
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Reporter</Typography>
                <Typography variant="body2">
                  {selected.reporter_name || 'Unknown'}{selected.reporter_email ? ` (${selected.reporter_email})` : ''}
                </Typography>
              </Box>
              <FormControl size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
                  {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{cap(s)}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select label="Priority" value={editPriority} onChange={(e) => setEditPriority(e.target.value)}>
                  {PRIORITY_OPTIONS.map((p) => <MenuItem key={p} value={p}>{cap(p)}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField
                label="Resolution note"
                multiline
                minRows={2}
                fullWidth
                value={editNote}
                onChange={(e) => setEditNote(e.target.value)}
                placeholder="What action was taken / why dismissed"
              />
              {selected.reviewed_by_name && (
                <Typography variant="caption" color="text.secondary">
                  Last reviewed by {selected.reviewed_by_name}
                  {selected.reviewed_at ? ` on ${new Date(selected.reviewed_at).toLocaleString()}` : ''}
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAction} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveAction}
            disabled={saving}
            sx={{ bgcolor: '#2D6E6A', '&:hover': { bgcolor: '#1F4E4A' } }}
          >
            {saving ? 'Saving\u2026' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContentModerationTab;
